/**
 * Spanish (Mexico) locale for devices-ui.
 * Export is the "devices" subtree: host apps merge this into their i18n
 * resources as translation.devices so that t('devices.list.heading') etc. resolve.
 */
import type { DevicesLocale } from './en'

export const devicesEsMx = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `Cancelar`,
    delete: `Eliminar`,
    save: `Guardar`,
    saveAndReboot: `Guardar y Reiniciar`,
    actions: `Acciones`,
    error: `Error`,
    yes: `Sí`,
    no: `No`,
    all: `Todos`,
    unchanged: `Sin cambios`,
    loading: `Cargando...`,
    none: `— Ninguno —`,
    backToList: `Volver a la lista`,
  },

  // ---------------------------------------------------------------------------
  // Options used across multiple components
  // ---------------------------------------------------------------------------
  options: {
    transportUdp: `UDP`,
    transportTcp: `TCP`,
    transportTls: `TLS`,
    jitterNormal: `Normal`,
    jitterLow: `Bajo`,
    jitterHigh: `Alto`,
    natNormal: `Normal`,
    natStun: `STUN`,
    serverGroupPrimary: `Primario`,
    serverGroupSecondary: `Secundario`,
    sourceMango: `Mango New`,
    sourceRefurbished: `Mango Refurbished`,
    sourceCustomer: `Cliente`,
    wifiSecurityNone: `NINGUNO`,
    wifiSecurityWep: `WEP`,
    wifiSecurityWpa: `WPA/WPA2 PSK`,
    wifiSecurityWpa3: `WPA3-Personal`,
    timezoneAlaska: `Alaska`,
    timezoneArizona: `Arizona`,
    timezoneAtlantic: `Atlantic Time (Canada)`,
    timezoneCentral: `Central Time (US & Canada)`,
    timezoneEastern: `Eastern Time (US & Canada)`,
    timezoneGreenwich: `Greenwich Mean Time (UK)`,
    timezoneHawaii: `Hawaii`,
    timezoneIndiana: `Indiana (East)`,
    timezoneMountain: `Mountain Time (US & Canada)`,
    timezonePacific: `Pacific Time (US & Canada)`,
    timezonePuertoRico: `Puerto Rico`,
  },

  // ---------------------------------------------------------------------------
  // Device form (add / edit)
  // ---------------------------------------------------------------------------
  form: {
    addDevice: `Agregar dispositivo`,
    editDevice: `Editar dispositivo`,
    createDevice: `Crear dispositivo`,
    loadingDevice: `Cargando dispositivo...`,

    // Section headers
    deviceInfo: `Información del Dispositivo`,
    advanced: `Avanzado`,
    displaySettings: `Configuración de Pantalla`,

    // Field labels
    model: `Modelo`,
    selectModel: `Seleccionar modelo de dispositivo…`,
    mostCommonYealink: `Más Común (Yealink)`,
    macAddress: `Dirección MAC`,
    macPlaceholder: `AA:BB:CC:DD:EE:FF`,
    generateRandomMac: `Generar dirección MAC aleatoria`,
    generateRandomMacConfirmTitle: `¿Reemplazar dirección MAC?`,
    generateRandomMacConfirmDescription: `Generar una dirección MAC aleatoria reemplazará el valor actual. ¿Está seguro de que desea continuar?`,
    generateRandomMacConfirmAction: `Generar nueva MAC`,
    copyProvisioningMac: `Copiar MAC para URL de aprovisionamiento`,
    copyProvisioningMacHint: `Copiar en minúsculas, sin dos puntos (para URL de aprovisionamiento)`,
    copyProvisioningMacCopied: `Copiado {{value}}`,
    copyProvisioningMacFailed: `Error al copiar. Seleccione el valor manualmente.`,
    checkingUniqueness: `Verificando unicidad…`,
    macRequired: `La dirección MAC es obligatoria`,
    macInvalid: `La dirección MAC no es válida`,
    macAlreadyInUse: `La dirección MAC ya está en uso`,
    modelRequired: `El modelo del dispositivo es obligatorio.`,
    warrantyExpiresAt: `Garantía Expira El`,
    source: `Fuente`,
    lastProvisionedTime: `Última Aprovisionamiento`,
    lastProvisionedFrom: `Último Aprovisionamiento Desde`,
    assignedUser: `Usuario Asignado`,
    assignedUserRequired: `El usuario asignado es obligatorio.`,
    directory: `Directorio`,
    directoryRequired: `El directorio es obligatorio.`,
    selectDirectory: `Seleccionar directorio...`,
    primaryLineExtension: `Extensión de Línea Principal`,
    noExtensionsForUser: `El usuario asignado no tiene extensiones de línea válidas. Selecciona otro usuario o asigna una extensión de línea antes de guardar.`,
    primaryLineExtensionRequired: `La extensión de línea principal es obligatoria.`,
    timeZone: `Zona Horaria`,
    stationName: `Nombre de Estación`,
    stationNameRequired: `El nombre de la estación es obligatorio.`,
    stationNamePlaceholder: `ej. Recepción`,
    enableDaylightSavings: `Habilitar Horario de Verano`,
    cannotSaveTitle: `Aún no se puede guardar`,
    buttonConfigurationInvalid: `Corrige la configuración de botones no válida antes de guardar.`,

    // Advanced section labels
    pages: `Páginas`,
    lineKeysAvailable: `Teclas de Línea Disponibles`,
    selectModelFirst: `Seleccionar Modelo Primero...`,
    lineKeysHelper: `({{pages}} página{{pageSuffix}} x {{keysPerPage}} teclas = {{total}} total)`,
    allowProvisioning: `Permitir Aprovisionamiento`,
    sidecarEnabled: `Módulo de Expansión Habilitado`,
    transport: `Transporte`,
    jitter: `Jitter`,
    serverGroupHost: `Host del Grupo de Servidores`,
    natTraversal: `Traversal NAT`,
    wifiEnabled: `WiFi Habilitado`,
    wifiNetworkSsid: `SSID de Red WiFi`,
    wifiSecurityMode: `Modo de Seguridad WiFi`,
    wifiPassword: `Contraseña WiFi`,
    acousticShieldEnabled: `Escudo Acústico Habilitado`,
  },

  // ---------------------------------------------------------------------------
  // Device preview card
  // ---------------------------------------------------------------------------
  preview: {
    title: `Vista Previa del Dispositivo`,
    unavailable: `Vista previa no disponible para el modelo seleccionado.`,
    emptyAlt: `Vista previa de dispositivo vacía`,
  },

  // ---------------------------------------------------------------------------
  // Button grid
  // ---------------------------------------------------------------------------
  buttons: {
    linesAndButtons: `Líneas y Botones`,
    sidecarButtons: `Botones del Módulo de Expansión`,
    lineButtonHelper_one: `{{count}} ranura de botón para {{modelName}}. La fila 1 siempre es Línea y sigue la Extensión de Línea Principal.`,

    lineButtonHelper_other: `{{count}} ranuras de botones para {{modelName}}. La fila 1 siempre es Línea y sigue la Extensión de Línea Principal.`,
    sidecarButtonHelper_one: `{{count}} ranura del módulo de expansión.`,

    sidecarButtonHelper_other: `{{count}} ranuras del módulo de expansión.`,
    noSlotsForModel: `No hay ranuras de botones disponibles para este modelo.`,
    selectModelForSlots: `Seleccione un modelo de dispositivo para ver las ranuras de botones.`,
    columnNumber: `#`,
    columnFunction: `Función`,
    columnValue: `Valor`,
    columnLabel: `Etiqueta`,
    phoneNumberPlaceholder: `Número de teléfono`,
    optionalLabelPlaceholder: `Etiqueta opcional`,
    extensionNone: `-- Ninguno --`,

    // Button function options
    functionBlank: `En blanco`,
    functionLine: `Línea`,
    functionRollover: `Rollover`,
    functionSpeedDial: `Marcación Rápida`,
    functionPresence: `BLF / Presencia`,
    functionParkingLot: `Estacionamiento`,
    functionSharedVoicemail: `Buzón de Voz Compartido`,

    // Preset speed dial options
    presetMangoSupport: `Soporte Mango`,
    presetLoginQueue: `Iniciar Sesión en Cola`,
    presetLogoutQueue: `Cerrar Sesión de Cola`,
    presetBlockCaller: `Bloquear Llamante`,
    presetOpenClose: `Abrir/Cerrar`,
    presetPauseResumeRecording: `Pausar/Reanudar Grabación`,
    presetAlternateVm: `Buzón de Voz Alternativo`,
    preset911History: `Historial 911`,

    // Preset button labels
    presetLabelMangoSupport: `Soporte Mango`,
    presetLabelLoginQ: `Entrar Q`,
    presetLabelLogoutQ: `Salir Q`,
    presetLabelBlock: `Bloquear`,
    presetLabelOpenClose: `Abrir/Cerrar`,
    presetLabelPauseResume: `Pausar/Reanudar`,
    presetLabelAltVm: `Alt VM`,
    presetLabel911Hist: `911 Hist`,

    parkingLotOption: `Estacionamiento {{number}}`,

    multipleLineWarningTitle: `Se detectaron múltiples botones de Línea`,
    multipleLineWarning: `Agregar múltiples botones de Línea puede causar resultados no deseados. Asegúrate de que la extensión que selecciones no esté asignada a otro botón de Línea y no esté configurada en ningún otro dispositivo.`,
    multipleLineWarningProceed: `Continuar`,
    multipleLineWarningCancel: `Cancelar`,
  },

  // ---------------------------------------------------------------------------
  // Clone dialog
  // ---------------------------------------------------------------------------
  clone: {
    title: `Clonar dispositivo`,
    description: `Crear un nuevo dispositivo desde "{{sourceLabel}}". Ingrese una dirección MAC única. Todas las configuraciones y botones se copiarán al nuevo dispositivo.`,
    loadingSettings: `Cargando configuración del dispositivo…`,
    stationLabelPlaceholder: `Clon del dispositivo`,
    cloneButton: `Clonar dispositivo`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `Eliminar dispositivo`,
    confirmation: `¿Está seguro de que desea eliminar "{{name}}"? Esta acción no se puede deshacer.`,
    unnamedDevice: `Dispositivo sin nombre`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `Edición Masiva de Dispositivos`,
    description_one: `Actualizando {{count}} dispositivo. Deja un campo sin cambios para conservar el valor actual.`,

    description_other: `Actualizando {{count}} dispositivos. Deja un campo sin cambios para conservar el valor actual.`,
    transport: `Transporte`,
    natTraversal: `Traversal NAT`,
    jitter: `Jitter`,
    timeZone: `Zona Horaria`,
    daylightSaving: `Horario de Verano`,
    allowProvisioning: `Permitir Aprovisionamiento`,
    serverGroupHost: `Host del Grupo de Servidores`,
    saveButton_one: `Guardar {{count}} dispositivo`,

    saveButton_other: `Guardar {{count}} dispositivos`,
  },

  // ---------------------------------------------------------------------------
  // Set desired firmware dialog
  // ---------------------------------------------------------------------------
  setFirmware: {
    title: `Establecer versión de firmware deseada`,
    description: `Establezca la versión de firmware deseada para {{count}} dispositivo(s) seleccionado(s). La configuración se recargará después de la actualización.`,
    label: `Versión de firmware deseada`,
    placeholder: `ej. 1.2.3.4`,
    confirmButton: `Establecer y recargar`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    heading: `Dispositivos`,
    searchPlaceholder: `Buscar por nombre, usuario, extensión o MAC...`,
    addDevice: `Agregar dispositivo`,
    actionsButton: `Acciones`,
    actionsWithCount: `Acciones ({{count}} seleccionado{{suffix}})`,
    massEditAction: `Edición Masiva ({{count}})`,
    reloadConfigAction: `Recargar configuración ({{count}})`,
    reloadConfigRebootAction: `Recargar config y reiniciar ({{count}})`,
    setFirmwareAction: `Establecer firmware deseado ({{count}})`,
    rebuildAll: `Reconstruir Todo`,
    rebuildAndReboot: `Reconstruir y Reiniciar`,
    rebuildAllConfirmTitle: `¿Reconstruir las configuraciones de todos los dispositivos?`,
    rebuildAllConfirmDescription: `Está a punto de reconstruir la configuración de todos los dispositivos en este PBX. ¿Está seguro de que desea continuar?`,
    rebuildAllConfirmAction: `Reconstruir todo`,
    rebuildAndRebootConfirmTitle: `¿Reconstruir y reiniciar todos los dispositivos?`,
    rebuildAndRebootConfirmDescription: `Está a punto de reconstruir la configuración de todos los dispositivos en este PBX y reiniciarlos. ¿Está seguro de que desea continuar?`,
    rebuildAndRebootConfirmAction: `Reconstruir y reiniciar`,
    pbxRequired: `Se requiere PBX para la página de Dispositivos.`,
    failedToLoad: `Error al cargar dispositivos`,
    noMatchingDevices: `Ningún dispositivo coincide con sus criterios de búsqueda`,

    // Column headers
    columnName: `Nombre`,
    columnPrimaryExt: `Ext principal`,
    columnUser: `Usuario`,
    columnModel: `Modelo`,
    columnMacAddress: `Dirección MAC`,
    columnFirmware: `Firmware`,
    columnWarranty: `Garantía`,
    columnLastCheckIn: `Última verificación`,
    columnInitializedOn: `Inicializado el`,
    columnConfiguration: `Configuración`,
    columnActions: `Acciones`,
    selectAllDevices: `Seleccionar todos los dispositivos`,
  },

  // ---------------------------------------------------------------------------
  // Device row
  // ---------------------------------------------------------------------------
  row: {
    loadingDevices: `Cargando dispositivos...`,
    noDevices: `Sin dispositivos`,
    noDevicesDescription: `Aún no hay dispositivos para este PBX.`,
    selectDevice: `Seleccionar {{name}}`,
    editDevice: `Editar {{name}}`,
    modelFallback: `Modelo {{id}}`,
    reportsModel: `Reporta: {{model}}`,
    warrantyInWarranty: `En garantía`,
    warrantyExpired: `Expirada`,
    warrantyUnknown: `Sin garantía`,
    changesPending: `Cambios Pendientes`,
    sidecar: `Módulo de Expansión`,
    noProv: `Sin Aprov`,
    fromSource: `de {{source}}`,
    desiredFirmware: `(deseado: {{version}})`,
    firmwareUnknown: `desconocido`,
    rowActions: `Acciones de fila`,
    cloneAction: `Clonar`,
    deleteAction: `Eliminar`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    make: `Marca`,
    model: `Modelo`,
    firmware: `Firmware`,
    firmwarePlaceholder: `Cualquiera`,
    warranty: `Garantía`,
    transport: `Transporte`,
    jitter: `Jitter`,
    sidecar: `Módulo de Expansión`,
    provisioning: `Aprovisionamiento`,
    clearFilters: `Limpiar filtros`,
    warrantyUnknown: `Sin garantía`,
    warrantyInWarranty: `En garantía`,
    warrantyExpired: `Expirada`,
    provisioningEnabled: `Habilitado`,
    provisioningDisabled: `Deshabilitado`,
    provisioningChangesPending: `Cambios pendientes`,
    toggleFilters: `Alternar filtros`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `Resultados por página:`,
    totalCount: `({{count}} en total)`,
    previousPage: `Página anterior`,
    nextPage: `Página siguiente`,
    all: `Todos`,
    jumpToPage: `Ir a la página {{page}}`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deviceCreated: `Dispositivo creado`,
    createFailed: `Error al crear dispositivo`,
    deviceUpdated: `Dispositivo actualizado`,
    deviceUpdatedRebooting: `Dispositivo actualizado y reiniciando`,
    updateFailed: `Error al actualizar dispositivo`,
  },
} as const satisfies DevicesLocale
